/**
 * Slider control for number endpoints with min/max
 *
 * Uses local state while dragging for UI responsiveness,
 * syncs to prop value when not actively dragging.
 */

import { useState, useEffect, useRef } from 'react';
import { formatValue } from '../utils';

interface SliderControlProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  color: string;
  onChange: (value: number) => void;
}

export function SliderControl({ value, min, max, step = 1, color, onChange }: SliderControlProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local value to prop when not dragging
  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  const commitEdit = () => {
    const parsed = parseFloat(editText);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div style={{ marginLeft: 'auto', minWidth: 100 }}>
        <input
          ref={inputRef}
          type="text"
          className="nodrag nopan"
          value={editText}
          onChange={e => setEditText(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: 10,
            color: '#e5e7eb',
            backgroundColor: '#1a1a2e',
            border: '1px solid #3b82f6',
            borderRadius: 2,
            padding: '1px 4px',
            outline: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minWidth: 100
      }}
      onDoubleClick={() => {
        setEditText(String(localValue));
        setIsEditing(true);
      }}
    >
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={localValue}
        onChange={handleChange}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        style={{
          width: 60,
          height: 4,
          cursor: 'pointer',
          accentColor: color
        }}
        className="nodrag"
      />
      <span style={{
        color: '#e5e7eb',
        fontFamily: 'monospace',
        fontSize: 10,
        minWidth: 30,
        textAlign: 'right'
      }}>
        {formatValue(localValue)}
      </span>
    </div>
  );
}
